import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useActiveStore } from '../../context/ActiveStoreContext';
import { ThreeDots } from 'react-loader-spinner';
import axios from 'axios';
import InvitationModal from '../invitations/InvitationModal';
import { hasAnyConnectedStore } from '../../utils/storeConnection';

interface UnifiedGuardProps {
  children: React.ReactNode;
}

// A user has "shared" access when they belong to a store as a non-owner
// (invited team member). Covers every non-owner role the backend can assign:
// member, admin, manager, analyst. These users can never connect a store, so
// the connection on-boarding screen must never be forced on them.
export const hasSharedStoreAccess = (stores: any[]) => {
  const storesList = stores || [];
  const sharedRoles = ['member', 'admin', 'manager', 'analyst'];
  return storesList.some((store: any) =>
    sharedRoles.includes(store.user_role) && store.shop !== 'temp-store'
  );
};

export default function UnifiedGuard({ children }: UnifiedGuardProps) {
  const { user, stores, loading: authLoading } = useAuth();
  const { activeStore } = useActiveStore();
  const location = useLocation();

  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [bootTimedOut, setBootTimedOut] = useState(false);

  // ─── Stable primitives derived from objects ───────────────────────────────
  // Using object references (user, stores, activeStore) in the dep array means
  // the effect re-runs on every render because React sees a new reference each
  // time, even when the data is identical. We derive stable primitives instead.
  const userId = user?.id ?? null;
  const userSkip = user?.skip ?? null;

  // ─── Boot watchdog ───────────────────────────────────────────────────────
  // authLoading (AuthContext) can hang if the /me request never resolves. This
  // timer guarantees the blocking loader is released even when that happens.
  useEffect(() => {
    const id = window.setTimeout(() => setBootTimedOut(true), 8000);
    return () => clearTimeout(id);
  }, []);

  // Derive shopToCheck as a stable string outside the effect
  const shopToCheck = (() => {
    if (activeStore?.shop && activeStore.shop !== 'temp-store') return activeStore.shop;
    const connected = (stores || []).find(
      (s: any) => s.connected && s.shop !== 'temp-store'
    );
    return connected?.shop ?? 'default';
  })();

  // Derive whether user has shared access as a stable boolean
  const storesHaveSharedAccess = hasSharedStoreAccess(stores || []);

  // ─── Deduplication refs ───────────────────────────────────────────────────
  // Prevent re-fetching when the shop / user haven't actually changed.
  const lastFetchKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    // Skip during logout
    if (typeof window !== 'undefined' && localStorage.getItem('is_logging_out') === 'true') {
      setIsLoading(false);
      return;
    }

    // No user yet (still booting) - wait for authLoading to finish
    if (authLoading) return;

    if (!userId) {
      setIsLoading(false);
      return;
    }

    // ─── KEY FIX: build a cache key from stable primitives.
    // If the key hasn't changed, we already have the right data - skip the fetch.
    const fetchKey = `${userId}:${shopToCheck}:${storesHaveSharedAccess}`;
    if (lastFetchKeyRef.current === fetchKey && subscriptionStatus !== null) {
      setIsLoading(false);
      return;
    }

    // Prevent concurrent fetches (React Strict Mode double-invoke, fast navigation)
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    // ─── CRITICAL: do NOT call setIsLoading(true) here.
    // Calling it inside the effect triggers a re-render, which re-runs the
    // effect, which calls setIsLoading(true) again → infinite loop.
    // isLoading starts as true from useState(true) and is only set to false
    // in the finally block once we have real data.

    const timeoutId = window.setTimeout(() => {
      // console.log('UnifiedGuard: Loading timeout, forcing loader off');
      setIsLoading(false);
      isFetchingRef.current = false;
    }, 5000);

    const run = async () => {
      try {
        if (storesHaveSharedAccess) {
          // Shared store users are always treated as active - no subscription call needed
          setSubscriptionStatus('active');
        } else {
          try {
            // console.log(`🔍 Checking subscription for shop: ${shopToCheck}`);
            const subResponse = await axios.get('/subscription/status', {
              params: { shop: shopToCheck },
            });
            // console.log(`🔍 Subscription response:`, subResponse.data);
            setSubscriptionStatus(subResponse.data.status);
          } catch {
            setSubscriptionStatus('none');
          }
        }

        try {
          const inviteResponse = await axios.get('/invitations/pending');
          setPendingInvitations(inviteResponse.data.invitations || []);
        } catch {
          setPendingInvitations([]);
        }

        lastFetchKeyRef.current = fetchKey;
      } finally {
        clearTimeout(timeoutId);
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    void run();

  // ─── Dep array: primitives only, never objects ────────────────────────────
  // userId        → number | null      (stable by value)
  // userSkip      → number | null      (stable by value)
  // shopToCheck   → string             (stable by value)
  // storesHaveSharedAccess → boolean   (stable by value)
  // authLoading   → boolean            (stable by value)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userSkip, shopToCheck, storesHaveSharedAccess, authLoading]);

  // ─── Route lists ──────────────────────────────────────────────────────────
  const alwaysAllowedRoutes = [
    '/my-reports/render',
    '/signin',
    '/signup',
    '/forgot-password',
    '/password/reset',
    '/reset-password',
    '/setup-account',
    '/auth/isight-handshake',
    '/shopify-app',
    '/admin',
    '/impersonate-login',
    '/email-template',
    '/guest-store',
    '/profile',
    '/support',
    '/on-boarding',
    '/billing/choose-plan',
  ];

  const isAlwaysAllowed = alwaysAllowedRoutes.some(route =>
    location.pathname.startsWith(route)
  );

  // ─── Helpers (pure - no state reads that could cause re-renders) ──────────
  const getUserRole = () => {
    const storesList = stores || [];
    if (storesList.length === 0) return 'none';
    if (storesList.some((s: any) => s.user_role === 'owner' && s.connected && s.shop !== 'temp-store')) return 'owner';
    if (storesList.some((s: any) => s.user_role === 'admin' && s.shop !== 'temp-store')) return 'admin';
    if (storesList.some((s: any) => s.user_role === 'member' && s.shop !== 'temp-store')) return 'member';
    return 'none';
  };

  const hasConnectedStore = () => {
    const storesList = stores || [];
    const fromReact = storesList.some((s: any) =>
      s.connected === true && s.shop !== 'temp-store' && s.user_role === 'owner'
    );
    if (fromReact) return true;
    // Fallback to localStorage: covers the first post-login render before React
    // commits the setStoresState update from login().
    try {
      const local = JSON.parse(localStorage.getItem('stores') || '[]');
      return local.some((s: any) =>
        s.connected === true && s.shop !== 'temp-store' && s.user_role === 'owner'
      );
    } catch { return false; }
  };

  const hasValidStoreAccess = () => hasConnectedStore() || storesHaveSharedAccess;

  const hasRecentlyAcceptedInvitation = () => {
    if (typeof window === 'undefined') return false;
    try {
      const invitationData = localStorage.getItem('invitation_accepted');
      const acceptanceTime = localStorage.getItem('invitation_accepted_time');
      if (invitationData && acceptanceTime) {
        return Date.now() - parseInt(acceptanceTime) < 60 * 60 * 1000;
      }
    } catch { /* ignore */ }
    return false;
  };

  const needsOnboarding = () => {
    const skipValue = Number(user?.skip) || 0;
    const hasRecentInvitation = hasRecentlyAcceptedInvitation();
    const hasValidConnectedStore = hasConnectedStore();
    // Invited team members (non-owner shared access) never see the connection
    // on-boarding screen - they cannot connect a store, that is the owner's job.
    const needsOnboard =
      skipValue === 0 &&
      !hasRecentInvitation &&
      !hasValidConnectedStore &&
      !storesHaveSharedAccess;
    // console.log(`🔍 Onboarding Check: skip=${skipValue}, needsOnboard=${needsOnboard}, hasRecentInvitation=${hasRecentInvitation}, hasValidConnectedStore=${hasValidConnectedStore}, sharedAccess=${storesHaveSharedAccess}`);
    return needsOnboard;
  };

  // ─── Loading state ────────────────────────────────────────────────────────
  // console.log(`🔍 Loading Check: isLoading=${isLoading}, authLoading=${authLoading}, isAlwaysAllowed=${isAlwaysAllowed}`);
  // console.log(`🔍 User Data: user=${!!user}, stores=${stores?.length || 0}, subscriptionStatus=${subscriptionStatus}`);

  // Returning users have session data already hydrated from localStorage.
  // Skip the blocking spinner for them - auth/subscription checks complete in the background.
  const hasLocalState = !!userId && !!user && (stores?.length ?? 0) > 0;

  if ((isLoading || authLoading) && !isAlwaysAllowed && !hasLocalState && !bootTimedOut) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          {/* <Loader className="w-20 h-20 mx-auto mb-4" /> */}
          {/* <p className="text-gray-600">Checking access...</p> */}
        </div>
      </div>
    );
  }

  // Always-allowed routes pass through immediately
  if (isAlwaysAllowed) return <>{children}</>;

  // No user - redirect to signin
  if (!user) {
    // console.log(`🚀 UnifiedGuard: No user, redirecting to /signin`);
    return <Navigate to="/signin" replace />;
  }

  // ─── Access logic ─────────────────────────────────────────────────────────
  const hasInvitationTokenVar = typeof window !== 'undefined' && localStorage.getItem('invitation_token');
  const hasAcceptedInvitation = typeof window !== 'undefined' && localStorage.getItem('invitation_accepted');

  const hasPendingInvitationsForCurrentUser = () => {
    if (!user?.email) return false;
    return pendingInvitations.some(inv =>
      (inv.email === user.email || inv.user_id === user.id) && inv.status === 'pending'
    );
  };

  const isTokenForCurrentUser = () => !!hasInvitationTokenVar && !!user?.email;

  const shouldShowInvitationModal = () => {
    const hasPending = hasPendingInvitationsForCurrentUser();
    const isTokenValid = isTokenForCurrentUser();
    const shouldShow = (hasPending || isTokenValid || hasAcceptedInvitation) && !hasRecentlyAcceptedInvitation();
    // console.log(`🔍 UnifiedGuard: Invitation modal decision:`, {
    //   hasPendingInvitationsForCurrentUser: hasPending,
    //   hasInvitationToken: !!hasInvitationTokenVar,
    //   isTokenValid,
    //   hasAcceptedInvitation: !!hasAcceptedInvitation,
    //   hasRecentlyAccepted: hasRecentlyAcceptedInvitation(),
    //   shouldShowModal: shouldShow,
    // });
    return shouldShow;
  };

  const userRole = getUserRole();
  const hasConnected = hasConnectedStore();
  const hasValidAccess = hasValidStoreAccess();
  const needsOnboard = needsOnboarding();

  const shouldSkipSubscriptionCheck = userRole === 'member' || userRole === 'admin';

  // console.log(`🔍 UnifiedGuard DEBUG: User role: ${userRole}, Should skip subscription: ${shouldSkipSubscriptionCheck}, Subscription: ${subscriptionStatus}, Skip: ${user?.skip}, Path: ${location.pathname}`);

  // 1. Onboarding redirect
  if (needsOnboard && location.pathname !== '/on-boarding' && !shouldShowInvitationModal()) {
    // console.log(` UnifiedGuard: User needs onboarding, redirecting to /on-boarding`);
    return <Navigate to="/on-boarding" replace />;
  }

  // 2. Keep user on onboarding if no connected store yet
  if (needsOnboard && !hasConnected && location.pathname === '/on-boarding' && !shouldShowInvitationModal()) {
    return <>{children}</>;
  }

  // 3. Owner has connected store but no plan - go to choose plan
  // Only redirect to Choose Plan when a store is genuinely connected:
  //   - Shopify store must have an access_token
  //   - WordPress/WooCommerce store must have connection_status === 'connected'
  if (needsOnboard && hasConnected && subscriptionStatus !== 'active' && location.pathname !== '/billing/choose-plan' && !shouldShowInvitationModal() && userRole === 'owner') {
    // console.log(`UnifiedGuard: Owner has connected store, redirecting to choose plan`);
    return <Navigate to="/billing/choose-plan" replace />;
  }

  // 4. No active subscription - redirect to choose plan ONLY when a store is connected.
  // If no store is connected yet (Shopify without access_token OR WordPress not
  // connected), send the user to onboarding so they can connect a platform first.
  if (!shouldSkipSubscriptionCheck && subscriptionStatus !== null && subscriptionStatus !== 'active' && location.pathname !== '/billing/choose-plan' && !needsOnboard) {
    if (!hasAnyConnectedStore(stores)) {
      // console.log(` UnifiedGuard: No connected store, redirecting to /on-boarding`);
      return <Navigate to="/on-boarding" replace />;
    }
    // console.log(` UnifiedGuard: No active subscription, redirecting to /billing/choose-plan`);
    return <Navigate to="/billing/choose-plan" replace />;
  }

  // 5. Final access check
  const allowAccess =
    location.pathname === '/on-boarding' ||
    location.pathname === '/billing/choose-plan' ||
    location.pathname.startsWith('/profile') ||
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/support') ||
    (Number(user?.skip) === 1 && hasValidAccess && (subscriptionStatus === 'active' || subscriptionStatus === null)) ||
    storesHaveSharedAccess ||
    subscriptionStatus === 'active' ||
    (hasValidAccess && subscriptionStatus === null) || // subscription still loading - allow tentatively, step 4 will redirect once data arrives
    hasRecentlyAcceptedInvitation() ||
    (needsOnboard && location.pathname === '/on-boarding');

  if (!allowAccess) {
    // console.log(`🚫 UnifiedGuard: Access denied to ${location.pathname}`);
    if (needsOnboard) return <Navigate to="/on-boarding" replace />;
    if (!shouldSkipSubscriptionCheck && subscriptionStatus !== 'active') return <Navigate to="/billing/choose-plan" replace />;
    return <Navigate to="/profile" replace />;
  }

  // console.log(`✅ UnifiedGuard: Allowing access to ${location.pathname}`);
  // console.log(`📋 FINAL CHECK - User skip: ${user?.skip}, Role: ${userRole}, Has connected: ${hasConnected}, Has valid access: ${hasValidAccess}, Subscription: ${subscriptionStatus}`);
  // console.log(`📋 Should skip subscription check: ${shouldSkipSubscriptionCheck}, Is scaleboard: ${location.pathname.startsWith('/scaleboard')}`);

  return (
    <>
      {children}
      <InvitationModal
        isOpen={!!shouldShowInvitationModal()}
        onClose={() => { /* Modal handles its own closing */ }}
        invitations={pendingInvitations}
      />
    </>
  );
}